import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Divider,
  IconButton,
  Tabs,
  Tab,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Edit, Delete, Visibility, Save, Close, Add, Translate as TranslateIcon } from '@mui/icons-material';
import { customTourRequestService, globalService } from '../../services/firebase';
import { CustomTourRequest, CustomTourRequestPageData } from '../../types';
import { useTranslation } from 'react-i18next';
import { getTranslatedData } from '../../utils/getTranslatedData';
import { produce } from 'immer';
import 'react-quill/dist/quill.snow.css';

const statusOptions = [
  { value: 'pending', label: 'Beklemede' },
  { value: 'contacted', label: 'İletişime Geçildi' },
  { value: 'quoted', label: 'Teklif Verildi' },
  { value: 'booked', label: 'Rezervasyon Yapıldı' },
  { value: 'cancelled', label: 'İptal Edildi' },
];

const ManageCustomTourRequestsPage: React.FC = () => {
  const [requests, setRequests] = useState<CustomTourRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<CustomTourRequest | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pageData, setPageData] = useState<CustomTourRequestPageData | null>(null);
  const [pageEditMode, setPageEditMode] = useState(false);
  const [pageEditData, setPageEditData] = useState<CustomTourRequestPageData | null>(null);
  const [pageSaveLoading, setPageSaveLoading] = useState(false);
  const [pageSaveSuccess, setPageSaveSuccess] = useState<string | null>(null);
  const [selectedLang, setSelectedLang] = useState('tr');
  const { t } = useTranslation();

  useEffect(() => {
    fetchRequests();
    fetchPageData();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const data = await customTourRequestService.getCustomTourRequests();
      setRequests(data);
    } catch (err) {
      setError('Talepler yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPageData = async () => {
    try {
      const data = await customTourRequestService.getCustomTourRequestPageData();
      setPageData(data);
      setPageEditData(data);
    } catch (err) {
      setError('Sayfa verileri yüklenirken hata oluştu.');
    }
  };

  const handleView = (req: CustomTourRequest) => {
    setSelectedRequest(req);
    setDialogOpen(true);
    if (!req.isRead) {
      customTourRequestService.markAsRead(req.id);
    }
  };

  const handleTranslatableFieldChange = (section: string, field: string, value: string) => {
    setPageEditData(produce(pageEditData, (draft: any) => {
      if (!draft) return;
      
      let sectionDraft = draft[section];
      if (!sectionDraft) {
        sectionDraft = draft[section] = {};
      }

      const existingValue = sectionDraft[field];
      let fieldAsObject = {};

      if (typeof existingValue === 'object' && existingValue !== null) {
          fieldAsObject = existingValue;
      } else if (typeof existingValue === 'string') {
          fieldAsObject = { tr: existingValue };
      }
      
      sectionDraft[field] = {
          ...fieldAsObject,
          [selectedLang]: value,
      };
    }));
  };

  const handleTourTypeChange = (index: number, field: string, value: any, isTranslatable = false) => {
    setPageEditData(produce((draft: any) => {
      const tourType = draft.formSection.tourTypes[index];
      if (isTranslatable) {
        if (typeof tourType[field] !== 'object' || tourType[field] === null) {
          tourType[field] = { tr: tourType[field] || '' };
        }
        tourType[field][selectedLang] = value;
      } else {
        tourType[field] = value;
      }
    }));
  };

  const addTourType = () => {
    setPageEditData(produce((draft: any) => {
      if (!draft.formSection.tourTypes) {
        draft.formSection.tourTypes = [];
      }
      draft.formSection.tourTypes.push({
        id: `new-${Date.now()}`,
        name: { tr: 'Yeni Tur Tipi', en: 'New Tour Type', ar: 'نوع جولة جديد' },
        icon: 'MoreHoriz',
        isActive: true,
        order: draft.formSection.tourTypes.length,
      });
    }));
  };

  const deleteTourType = (index: number) => {
    setPageEditData(produce((draft: any) => {
      draft.formSection.tourTypes.splice(index, 1);
    }));
  };

  const handleStatusChange = async (id: string, status: string) => {
    await customTourRequestService.updateCustomTourRequest(id, { status });
    fetchRequests();
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Bu talebi silmek istediğinize emin misiniz?')) {
      await customTourRequestService.deleteCustomTourRequest(id);
      fetchRequests();
    }
  };

  const handlePageEditChange = (section: string, field: string, value: any) => {
    setPageEditData((prev: any) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleTranslateAll = async () => {
    if (!pageEditData) return;
    setPageSaveLoading(true);
    setError(null);

    try {
      // Clone data
      const dataCopy: any = JSON.parse(JSON.stringify(pageEditData));

      // Paths to translate (simple fields)
      const simplePaths = [
        'heroSection.title',
        'heroSection.subtitle',
        'heroSection.description',
        'formSection.title',
        'formSection.description',
        'processSection.title',
        'processSection.description',
        'whyChooseSection.title',
        'whyChooseSection.description'
      ];

      for (const path of simplePaths) {
        const keys = path.split('.');
        let parent: any = dataCopy;
        for (let i = 0; i < keys.length - 1; i++) parent = parent[keys[i]];
        const field = keys[keys.length - 1];
        const sourceText = getTranslatedData(parent[field], 'tr');
        if (sourceText) {
          const translations = await globalService.translateTexts({ text: sourceText, sourceLang: 'tr', targetLangs: ['en','ar'] });
          parent[field] = { tr: sourceText, en: (translations as any).en, ar: (translations as any).ar };
        }
      }

      // Translate arrays (tourTypes, steps, features)
      if (Array.isArray(dataCopy.formSection?.tourTypes)) {
        for (const type of dataCopy.formSection.tourTypes) {
          const sourceName = getTranslatedData(type.name, 'tr');
          if (sourceName) {
            const translations = await globalService.translateTexts({ text: sourceName, sourceLang: 'tr', targetLangs: ['en','ar'] });
            type.name = { tr: sourceName, en: (translations as any).en, ar: (translations as any).ar };
          }
        }
      }

      if (Array.isArray(dataCopy.processSection?.steps)) {
        for (const step of dataCopy.processSection.steps) {
          const srcTitle = getTranslatedData(step.title, 'tr');
          const srcDesc = getTranslatedData(step.description, 'tr');
          if (srcTitle) {
            const trn = await globalService.translateTexts({ text: srcTitle, sourceLang: 'tr', targetLangs: ['en','ar'] });
            step.title = { tr: srcTitle, en: (trn as any).en, ar: (trn as any).ar };
          }
          if (srcDesc) {
            const trn2 = await globalService.translateTexts({ text: srcDesc, sourceLang: 'tr', targetLangs: ['en','ar'] });
            step.description = { tr: srcDesc, en: (trn2 as any).en, ar: (trn2 as any).ar };
          }
        }
      }

      if (Array.isArray(dataCopy.whyChooseSection?.features)) {
        for (const feat of dataCopy.whyChooseSection.features) {
          const srcTitle = getTranslatedData(feat.title, 'tr');
          const srcDesc = getTranslatedData(feat.description, 'tr');
          if (srcTitle) {
            const trn = await globalService.translateTexts({ text: srcTitle, sourceLang: 'tr', targetLangs: ['en','ar'] });
            feat.title = { tr: srcTitle, en: (trn as any).en, ar: (trn as any).ar };
          }
          if (srcDesc) {
            const trn2 = await globalService.translateTexts({ text: srcDesc, sourceLang: 'tr', targetLangs: ['en','ar'] });
            feat.description = { tr: srcDesc, en: (trn2 as any).en, ar: (trn2 as any).ar };
          }
        }
      }

      setPageEditData(dataCopy);
      setPageSaveSuccess('Çeviri tamamlandı!');
    } catch (err) {
      console.error(err);
      setError('Çeviri sırasında hata oluştu.');
    } finally {
      setPageSaveLoading(false);
    }
  };

  const handlePageSave = async () => {
    setPageSaveLoading(true);
    setPageSaveSuccess(null);
    try {
      await customTourRequestService.updateCustomTourRequestPageData(pageEditData);
      setPageSaveSuccess('Sayfa içerikleri başarıyla güncellendi!');
      setPageEditMode(false);
      fetchPageData();
    } catch (err) {
      setError('Sayfa içerikleri güncellenirken hata oluştu.');
    } finally {
      setPageSaveLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight={700} mb={4}>
        Özel Tur Talepleri Yönetimi
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Talepler Tablosu */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" mb={2}>
            Gelen Talepler
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {requests.length === 0 && (
                <Box sx={{ width: '100%' }}>
                  <Typography color="text.secondary">Henüz talep yok.</Typography>
                </Box>
              )}
              {requests.map((req) => (
                <Box key={req.id} sx={{ width: { xs: '100%', md: '50%', lg: '33.33%' }, p: 1, boxSizing: 'border-box', display: 'inline-block', verticalAlign: 'top' }}>
                  <Card variant={req.isRead ? 'outlined' : 'elevation'} sx={{ borderLeft: req.isRead ? '4px solid #ccc' : '4px solid #1976d2' }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight={600}>{req.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{req.email} | {req.phone}</Typography>
                      <Typography variant="body2" color="text.secondary" mt={1}>Tur Tipi: <b>{req.tourType}</b></Typography>
                      <Typography variant="body2" color="text.secondary">Durum: <b>{statusOptions.find(s => s.value === req.status)?.label}</b></Typography>
                      <Typography variant="body2" color="text.secondary" mt={1} noWrap>Mesaj: {req.message}</Typography>
                    </CardContent>
                    <CardActions>
                      <Button size="small" startIcon={<Visibility />} onClick={() => handleView(req)}>
                        Görüntüle
                      </Button>
                      <TextField
                        select
                        size="small"
                        value={req.status}
                        onChange={e => handleStatusChange(req.id, e.target.value)}
                        sx={{ minWidth: 120 }}
                      >
                        {statusOptions.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </TextField>
                      <IconButton color="error" onClick={() => handleDelete(req.id)}>
                        <Delete />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Box>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Talep Detay Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Talep Detayı
          <IconButton onClick={() => setDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRequest && (
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>{selectedRequest.name}</Typography>
              <Typography variant="body2" color="text.secondary">{selectedRequest.email} | {selectedRequest.phone}</Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2"><b>Tur Tipi:</b> {selectedRequest.tourType}</Typography>
              <Typography variant="body2"><b>Destinasyon:</b> {selectedRequest.destination}</Typography>
              <Typography variant="body2"><b>Süre:</b> {selectedRequest.duration}</Typography>
              <Typography variant="body2"><b>Katılımcı:</b> {selectedRequest.participantCount}</Typography>
              <Typography variant="body2"><b>Bütçe:</b> {selectedRequest.budget}</Typography>
              <Typography variant="body2"><b>Tarihler:</b> {selectedRequest.preferredDates}</Typography>
              <Typography variant="body2"><b>Özel İstekler:</b> {selectedRequest.specialRequests}</Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2"><b>Mesaj:</b> {selectedRequest.message}</Typography>
              <Typography variant="body2" color="text.secondary" mt={2}><b>Durum:</b> {statusOptions.find(s => s.value === selectedRequest.status)?.label}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="primary">Kapat</Button>
        </DialogActions>
      </Dialog>

      {/* Sayfa İçeriği Yönetimi */}
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6">Sayfa İçerikleri</Typography>
            <Box>
              {!pageEditMode && (
                <Button startIcon={<Edit />} onClick={() => setPageEditMode(true)}>
                  Düzenle
                </Button>
              )}
            </Box>
          </Box>
          
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={selectedLang} onChange={(e, newValue) => setSelectedLang(newValue)}>
              <Tab label="Türkçe" value="tr" />
              <Tab label="English" value="en" />
              <Tab label="العربية" value="ar" />
            </Tabs>
          </Box>

          {pageEditMode ? (
            <Box>
              {/* Hero Section */}
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Ana Bölüm</Typography>
              <TextField label="Başlık" value={getTranslatedData(pageEditData?.heroSection?.title, selectedLang)} onChange={e => handleTranslatableFieldChange('heroSection', 'title', e.target.value)} fullWidth sx={{ mb: 2 }} />
              <TextField label="Alt Başlık" value={getTranslatedData(pageEditData?.heroSection?.subtitle, selectedLang)} onChange={e => handleTranslatableFieldChange('heroSection', 'subtitle', e.target.value)} fullWidth sx={{ mb: 2 }} />
              <TextField label="Açıklama" value={getTranslatedData(pageEditData?.heroSection?.description, selectedLang)} onChange={e => handleTranslatableFieldChange('heroSection', 'description', e.target.value)} fullWidth multiline rows={3} sx={{ mb: 2 }} />
              <TextField label="Arkaplan Görseli (URL)" value={pageEditData?.heroSection?.backgroundImage || ''} onChange={e => handlePageEditChange('heroSection', 'backgroundImage', e.target.value)} fullWidth sx={{ mb: 2 }} />
              <Divider sx={{ my: 2 }} />

              {/* Form Section */}
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Form Bölümü</Typography>
              <TextField label="Başlık" value={getTranslatedData(pageEditData?.formSection?.title, selectedLang)} onChange={e => handleTranslatableFieldChange('formSection', 'title', e.target.value)} fullWidth sx={{ mb: 2 }} />
              <TextField label="Açıklama" value={getTranslatedData(pageEditData?.formSection?.description, selectedLang)} onChange={e => handleTranslatableFieldChange('formSection', 'description', e.target.value)} fullWidth multiline rows={2} sx={{ mb: 2 }} />
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Tur Tipleri</Typography>
                {pageEditData?.formSection?.tourTypes?.map((tourType: any, index: number) => (
                  <Card key={tourType.id} sx={{ mb: 2, p: 2 }} variant="outlined">
                    <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
                      <Box sx={{ flex: '1 1 30%', minWidth: '200px' }}>
                        <TextField
                          label="Tur Tipi Adı"
                          value={getTranslatedData(tourType.name, selectedLang)}
                          onChange={(e) => handleTourTypeChange(index, 'name', e.target.value, true)}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 20%', minWidth: '150px' }}>
                        <TextField
                          label="Ikon"
                          value={tourType.icon}
                          onChange={(e) => handleTourTypeChange(index, 'icon', e.target.value)}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 10%', minWidth: '80px' }}>
                         <TextField
                          label="Sıra"
                          type="number"
                          value={tourType.order}
                          onChange={(e) => handleTourTypeChange(index, 'order', parseInt(e.target.value, 10))}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 15%', minWidth: '100px' }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={tourType.isActive}
                              onChange={(e) => handleTourTypeChange(index, 'isActive', e.target.checked)}
                            />
                          }
                          label="Aktif"
                        />
                      </Box>
                      <Box>
                        <IconButton onClick={() => deleteTourType(index)} color="error">
                          <Delete />
                        </IconButton>
                      </Box>
                    </Box>
                  </Card>
                ))}
                <Button startIcon={<Add />} onClick={addTourType} variant="outlined">
                  Yeni Tur Tipi Ekle
                </Button>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Process Section */}
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Süreç Bölümü</Typography>
              <TextField label="Başlık" value={getTranslatedData(pageEditData?.processSection?.title, selectedLang)} onChange={e => handleTranslatableFieldChange('processSection', 'title', e.target.value)} fullWidth sx={{ mb: 2 }} />
              <TextField label="Açıklama" value={getTranslatedData(pageEditData?.processSection?.description, selectedLang)} onChange={e => handleTranslatableFieldChange('processSection', 'description', e.target.value)} fullWidth multiline rows={2} sx={{ mb: 2 }} />
              <Divider sx={{ my: 2 }} />

              {/* Why Choose Section */}
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Neden Bizi Seçmelisiniz Bölümü</Typography>
              <TextField label="Başlık" value={getTranslatedData(pageEditData?.whyChooseSection?.title, selectedLang)} onChange={e => handleTranslatableFieldChange('whyChooseSection', 'title', e.target.value)} fullWidth sx={{ mb: 2 }} />
              <TextField label="Açıklama" value={getTranslatedData(pageEditData?.whyChooseSection?.description, selectedLang)} onChange={e => handleTranslatableFieldChange('whyChooseSection', 'description', e.target.value)} fullWidth multiline rows={2} sx={{ mb: 2 }} />
              
              <Box mt={3} display="flex" gap={2}>
                <Button variant="outlined" startIcon={<TranslateIcon />} onClick={handleTranslateAll} disabled={pageSaveLoading}>
                  {pageSaveLoading ? 'Çeviri Yapılıyor...' : 'Tümünü Çevir'}
                </Button>
                <Button variant="contained" startIcon={<Save />} onClick={handlePageSave} disabled={pageSaveLoading}>
                  {pageSaveLoading ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
                <Button variant="outlined" startIcon={<Close />} onClick={() => setPageEditMode(false)}>
                  İptal
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>{getTranslatedData(pageData?.heroSection?.title, selectedLang)}</Typography>
              <Typography variant="body2" color="text.secondary">{getTranslatedData(pageData?.heroSection?.subtitle, selectedLang)}</Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>{getTranslatedData(pageData?.heroSection?.description, selectedLang)}</Typography>
              <Box mt={2}>
                <img src={pageData?.heroSection?.backgroundImage} alt="Arkaplan" style={{ maxWidth: 400, borderRadius: 8 }} />
              </Box>
            </Box>
          )}
          {pageSaveSuccess && <Alert severity="success" sx={{ mt: 2 }}>{pageSaveSuccess}</Alert>}
        </CardContent>
      </Card>
    </Container>
  );
};

export default ManageCustomTourRequestsPage; 